import React, { useCallback, useEffect, useRef, useState } from "react";
import { ThreeEvent, useThree } from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import { FloorplannerStoreContext } from "../../../../../store/floorplannerStore";
import {
  DoorType,
  DoubleDoorType,
  SymbolType,
} from "../../../../../types/floorplannerTypes";
import { resolveMaterial } from "../../../materials/materialResolver";
import { shouldUseSymbolFillColorIn3D } from "../../../materials/primitiveSymbolFill";
import SelectedMeshOutline from "../../../scene/SelectedMeshOutline";
import { usePointerUpSelection } from "../../../scene/usePointerUpSelection";
import {
  Door3DAssemblyGeometry,
  Door3DLeafGeometry,
  clampDoorOpenAngle,
  getSymbolColor,
} from "../openingGeometry";
import { CANVAS_ACCENT_COLOR_HEX } from "../../../../../constants/brandColors";
import { resolveFillOpacity } from "../../../../../utils/fillOpacity";
import { getOpacityRemountEpoch } from "../../../../../utils/opacityRemount";
import DoorHandle3D from "./DoorHandle3D";
import DoorLeaf3D from "./DoorLeaf3D";
import DoorSlotMaterial from "./DoorSlotMaterial";
import {
  resolveDoorHandleStyle,
  resolveDoorStyle,
} from "./door3DStyle";
import { placeDoorHandleCenter } from "./doorPaneledLayout";

type Door3DSymbol = DoorType | DoubleDoorType;

const Door3DAssemblyMesh: React.FC<{
  symbol: Door3DSymbol;
  assembly: Door3DAssemblyGeometry;
  selected: boolean;
  claimSelectionEvents?: boolean;
  onSelect: (symbol: SymbolType, event: ThreeEvent<PointerEvent>) => void;
  onAngleDragActiveChange: (active: boolean) => void;
}> = observer(
  ({
    symbol,
    assembly,
    selected,
    claimSelectionEvents = false,
    onSelect,
    onAngleDragActiveChange,
  }) => {
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const { gl, invalidate } = useThree();
    const [draggingLeafId, setDraggingLeafId] = useState<string | null>(null);
    const draggingLeafIdRef = useRef<string | null>(null);
    const dragStartAngleRef = useRef(symbol.openAngle);
    const latestAngleRef = useRef(symbol.openAngle);
    const changedAngleRef = useRef(false);
    const leafGroupsRef = useRef(new Map<string, THREE.Group>());
    const selectionHandlers = usePointerUpSelection(symbol, onSelect, {
      claimPointerEvents: claimSelectionEvents,
    });

    useEffect(() => {
      draggingLeafIdRef.current = draggingLeafId;
    }, [draggingLeafId]);

    useEffect(() => {
      latestAngleRef.current = symbol.openAngle;
      applyOpenAngleToLeafGroups(
        assembly.leaves,
        leafGroupsRef.current,
        symbol.openAngle,
      );
    }, [symbol.openAngle]);

    useEffect(() => {
      applyOpenAngleToLeafGroups(
        assembly.leaves,
        leafGroupsRef.current,
        latestAngleRef.current,
      );
    }, [assembly.leaves]);

    const completeAngleDrag = useCallback(
      (updateState = true) => {
        if (!draggingLeafIdRef.current) return;

        draggingLeafIdRef.current = null;
        if (updateState) {
          setDraggingLeafId(null);
        }
        onAngleDragActiveChange(false);
        gl.domElement.style.cursor = "default";

        if (
          changedAngleRef.current &&
          Math.abs(latestAngleRef.current - dragStartAngleRef.current) > 1e-5
        ) {
          floorplannerStore.updateSymbolProperty(
            symbol.id,
            "openAngle",
            latestAngleRef.current,
          );
          floorplannerStore.recordSymbolChange(symbol.id, "modify");
          floorplannerStore.pushToUndoStack();
          floorplannerStore.setBlockDirty(false);
          floorplannerStore.setDirty();
        } else {
          floorplannerStore.cancelUndoTransaction();
          floorplannerStore.setBlockDirty(false);
        }
        invalidate();
      },
      [
        floorplannerStore,
        gl.domElement.style,
        invalidate,
        onAngleDragActiveChange,
        symbol.id,
      ],
    );

    const completeAngleDragRef = useRef(completeAngleDrag);
    useEffect(() => {
      completeAngleDragRef.current = completeAngleDrag;
    }, [completeAngleDrag]);

    useEffect(() => {
      if (!selected) completeAngleDrag();
    }, [completeAngleDrag, selected]);

    useEffect(
      () => () => {
        completeAngleDragRef.current(false);
      },
      [],
    );

    const finishAngleDrag = useCallback(
      (event: ThreeEvent<PointerEvent>) => {
        if (!draggingLeafIdRef.current) return;

        event.stopPropagation();
        event.nativeEvent.preventDefault();
        event.nativeEvent.stopImmediatePropagation?.();
        const pointerTarget = event.target as unknown as {
          releasePointerCapture?: (pointerId: number) => void;
        };
        try {
          pointerTarget.releasePointerCapture?.(event.pointerId);
        } catch {
          // The browser may already release capture before lostpointercapture.
        }
        completeAngleDrag();
      },
      [completeAngleDrag],
    );

    const updateOpenAngleFromPointer = useCallback(
      (leaf: Door3DLeafGeometry, event: ThreeEvent<PointerEvent>) => {
        if (draggingLeafId !== leaf.id) return;

        event.stopPropagation();
        event.nativeEvent.preventDefault();
        event.nativeEvent.stopImmediatePropagation?.();
        const nextPoint = getPointerPointOnZPlane(
          event,
          leaf.angleHandleCenter.z,
        );
        if (!nextPoint) return;

        const nextAngle = getDoorOpenAngleFromPointerPoint(
          assembly,
          leaf,
          nextPoint,
        );
        if (Math.abs(nextAngle - latestAngleRef.current) <= 1e-4) return;

        changedAngleRef.current = true;
        latestAngleRef.current = nextAngle;
        applyOpenAngleToLeafGroups(
          assembly.leaves,
          leafGroupsRef.current,
          nextAngle,
        );
        invalidate();
      },
      [assembly, draggingLeafId, invalidate],
    );

    const startAngleDrag = useCallback(
      (leaf: Door3DLeafGeometry, event: ThreeEvent<PointerEvent>) => {
        event.stopPropagation();
        event.nativeEvent.preventDefault();
        event.nativeEvent.stopImmediatePropagation?.();
        const pointerTarget = event.target as unknown as {
          setPointerCapture?: (pointerId: number) => void;
        };
        pointerTarget.setPointerCapture?.(event.pointerId);
        dragStartAngleRef.current = symbol.openAngle;
        latestAngleRef.current = symbol.openAngle;
        changedAngleRef.current = false;
        floorplannerStore.beginUndoTransaction("updateOpenAngle3D");
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.setBlockDirty(true);
        draggingLeafIdRef.current = leaf.id;
        setDraggingLeafId(leaf.id);
        onAngleDragActiveChange(true);
        gl.domElement.style.cursor = "grabbing";
        invalidate();
      },
      [
        floorplannerStore,
        gl.domElement.style,
        invalidate,
        onAngleDragActiveChange,
        symbol.id,
        symbol.openAngle,
      ],
    );

    const panelColor = resolveMaterial("doorPanel", symbol, {
      defaultColor: getSymbolColor(symbol),
    }).color;
    const symbol3DOnly = { threeD: symbol.threeD };
    const symbolPartOwner = shouldUseSymbolFillColorIn3D(symbol)
      ? symbol
      : symbol3DOnly;
    const frameColor = resolveMaterial("doorFrame", symbolPartOwner, {
      defaultColor: panelColor.clone().lerp(new THREE.Color(0xffffff), 0.38),
    }).color;
    const casingColor = frameColor.clone().lerp(panelColor, 0.45);
    const grooveColor = frameColor.clone().multiplyScalar(0.72);
    const hardwareColor = resolveMaterial("doorHardware", symbolPartOwner, {
      defaultColor: 0xc4c8cc,
    }).color;
    /** Handles share beslag (doorHardware) with hinges and other metal fittings. */
    const handleColor = resolveMaterial("doorHardware", symbolPartOwner, {
      defaultColor: 0xc9cdd1,
    }).color;
    const keyholeColor = new THREE.Color(0x2f3337);
    const glassColor = resolveMaterial("windowGlass", symbol3DOnly, {
      defaultColor: 0xd2e8f5,
    }).color;
    const doorStyle = resolveDoorStyle(symbol.threeD?.doorStyle);
    const doorHandleStyle = resolveDoorHandleStyle(
      symbol.threeD?.doorHandleStyle,
    );
    const fillOpacity = resolveFillOpacity(symbol);
    const opacityMaterialProps = {
      opacity: fillOpacity,
      transparent: fillOpacity < 1,
      depthWrite: fillOpacity >= 1,
    };
    /**
     * Remount when Fill opacity changes so Three.js materials discard stale
     * scene-fade state (same path as 2D doors and furniture 3D).
     */
    const opacityRemountEpoch = getOpacityRemountEpoch(symbol.id);

    return (
      <group
        key={`opacity-remount-${opacityRemountEpoch}`}
        position={[assembly.origin.x, assembly.origin.y, 0]}
        rotation={[0, 0, assembly.rotation]}
        userData={{ floorplannerType: symbol.type, floorplannerId: symbol.id }}
        {...selectionHandlers}
        onPointerOver={() => {
          gl.domElement.style.cursor = "pointer";
        }}
        onPointerOut={() => {
          if (!draggingLeafId) gl.domElement.style.cursor = "default";
        }}
      >
        {assembly.frames.map((frame) => (
          <mesh
            key={frame.id}
            position={[frame.center.x, frame.center.y, frame.center.z]}
            rotation={[0, frame.rotationY ?? 0, 0]}
            castShadow
            receiveShadow
          >
            <boxGeometry args={[frame.size.x, frame.size.y, frame.size.z]} />
            <DoorSlotMaterial
              symbol={symbol}
              slot="doorFrame"
              fallbackColor={frameColor}
              fallbackRoughness={0.7}
              fallbackMetalness={0.05}
              {...opacityMaterialProps}
            />
            <SelectedMeshOutline visible={selected} />
          </mesh>
        ))}

        {assembly.stops.map((stop) => (
          <mesh
            key={stop.id}
            position={[stop.center.x, stop.center.y, stop.center.z]}
            rotation={[0, stop.rotationY ?? 0, 0]}
            castShadow
            receiveShadow
          >
            <boxGeometry args={[stop.size.x, stop.size.y, stop.size.z]} />
            <DoorSlotMaterial
              symbol={symbol}
              slot="doorFrame"
              fallbackColor={casingColor}
              fallbackRoughness={0.72}
              fallbackMetalness={0.02}
              {...opacityMaterialProps}
            />
            <SelectedMeshOutline visible={selected} />
          </mesh>
        ))}

        {assembly.casings.map((casing) => (
          <mesh
            key={casing.id}
            position={[casing.center.x, casing.center.y, casing.center.z]}
            rotation={[0, casing.rotationY ?? 0, 0]}
            castShadow
            receiveShadow
          >
            <boxGeometry args={[casing.size.x, casing.size.y, casing.size.z]} />
            <DoorSlotMaterial
              symbol={symbol}
              slot="doorFrame"
              fallbackColor={casingColor}
              fallbackRoughness={0.7}
              fallbackMetalness={0.02}
              {...opacityMaterialProps}
            />
            <SelectedMeshOutline visible={selected} />
          </mesh>
        ))}

        {assembly.miterGrooves.map((groove) => (
          <mesh
            key={groove.id}
            position={[groove.center.x, groove.center.y, groove.center.z]}
            rotation={[0, groove.rotationY ?? 0, 0]}
            castShadow
          >
            <boxGeometry args={[groove.size.x, groove.size.y, groove.size.z]} />
            <DoorSlotMaterial
              symbol={symbol}
              slot="doorFrame"
              fallbackColor={grooveColor}
              fallbackRoughness={0.78}
              fallbackMetalness={0}
              {...opacityMaterialProps}
            />
          </mesh>
        ))}

        {assembly.leaves.map((leaf) => (
          <group
            key={leaf.id}
            ref={(group) => {
              if (group) {
                leafGroupsRef.current.set(leaf.id, group);
              } else {
                leafGroupsRef.current.delete(leaf.id);
              }
            }}
            position={[leaf.hinge.x, leaf.hinge.y, 0]}
            rotation={[0, 0, leaf.rotation]}
          >
            <DoorLeaf3D
              symbol={symbol}
              style={doorStyle}
              width={leaf.width}
              thickness={leaf.thickness}
              height={leaf.height}
              center={leaf.panelCenter}
              panelColor={panelColor}
              frameColor={frameColor}
              glassColor={glassColor}
              fillOpacity={fillOpacity}
              opacityMaterialProps={opacityMaterialProps}
              selected={selected}
            />

            {leaf.hingeBarrels.map((barrel, index) => (
              <mesh
                key={`${leaf.id}-hinge-${index}`}
                position={[barrel.center.x, barrel.center.y, barrel.center.z]}
                rotation={[Math.PI / 2, 0, 0]}
                castShadow
                receiveShadow
              >
                <cylinderGeometry
                  args={[barrel.radius, barrel.radius, barrel.height, 16]}
                />
                <DoorSlotMaterial
                  symbol={symbol}
                  slot="doorHardware"
                  fallbackColor={hardwareColor}
                  fallbackRoughness={0.26}
                  fallbackMetalness={0.62}
                  {...opacityMaterialProps}
                />
                <SelectedMeshOutline visible={selected} />
              </mesh>
            ))}

            {leaf.handleCenters.map((handleCenter, index) => (
              <DoorHandle3D
                key={`${leaf.id}-handle-${index}`}
                symbol={symbol}
                style={doorHandleStyle}
                handleCenter={placeDoorHandleCenter(
                  doorStyle,
                  leaf,
                  handleCenter,
                )}
                leafWidth={leaf.width}
                leafThickness={leaf.thickness}
                leafHeight={leaf.height}
                bottomGap={leaf.bottomGap}
                leverDirection={-leaf.closedSign}
                handleColor={handleColor}
                keyholeColor={keyholeColor}
                opacityMaterialProps={opacityMaterialProps}
                selected={selected}
              />
            ))}

            {selected && (
              <group
                position={[
                  leaf.angleHandleCenter.x,
                  leaf.angleHandleCenter.y,
                  leaf.angleHandleCenter.z,
                ]}
                onPointerDown={(event) => startAngleDrag(leaf, event)}
                onPointerMove={(event) =>
                  updateOpenAngleFromPointer(leaf, event)
                }
                onPointerUp={finishAngleDrag}
                onPointerCancel={finishAngleDrag}
                onLostPointerCapture={finishAngleDrag}
                onPointerOver={(event) => {
                  event.stopPropagation();
                  gl.domElement.style.cursor = draggingLeafId
                    ? "grabbing"
                    : "grab";
                }}
                onPointerOut={(event) => {
                  event.stopPropagation();
                  if (!draggingLeafId) gl.domElement.style.cursor = "pointer";
                }}
              >
                <mesh>
                  <sphereGeometry args={[0.065, 20, 16]} />
                  <meshBasicMaterial color={CANVAS_ACCENT_COLOR_HEX} />
                </mesh>
                <mesh>
                  <sphereGeometry args={[0.12, 12, 10]} />
                  <meshBasicMaterial
                    transparent
                    opacity={0}
                    depthWrite={false}
                  />
                </mesh>
              </group>
            )}
          </group>
        ))}
      </group>
    );
  },
);

function getPointerPointOnZPlane(
  event: ThreeEvent<PointerEvent>,
  z: number,
): THREE.Vector3 | null {
  return event.ray.intersectPlane(
    new THREE.Plane(new THREE.Vector3(0, 0, 1), -z),
    new THREE.Vector3(),
  );
}

function getDoorOpenAngleFromPointerPoint(
  assembly: Door3DAssemblyGeometry,
  leaf: Door3DLeafGeometry,
  point: THREE.Vector3,
): number {
  const localPoint = new THREE.Vector2(point.x, point.y)
    .sub(assembly.origin)
    .rotateAround(new THREE.Vector2(0, 0), -assembly.rotation);
  const relativePoint = localPoint.sub(leaf.hinge);
  if (relativePoint.lengthSq() < 1e-8)
    return clampDoorOpenAngle(leaf.rotation / leaf.rotationSign);

  const baseAngle = leaf.closedSign === 1 ? 0 : Math.PI;
  const pointerAngle = Math.atan2(relativePoint.y, relativePoint.x);
  const signedDelta = normalizeSignedAngle(pointerAngle - baseAngle);
  return clampDoorOpenAngle(signedDelta / leaf.rotationSign);
}

function normalizeSignedAngle(angle: number): number {
  let normalized = angle;
  while (normalized <= -Math.PI) normalized += Math.PI * 2;
  while (normalized > Math.PI) normalized -= Math.PI * 2;
  return normalized;
}

function applyOpenAngleToLeafGroups(
  leaves: Door3DLeafGeometry[],
  leafGroups: Map<string, THREE.Group>,
  openAngle: number,
): void {
  const nextAngle = clampDoorOpenAngle(openAngle);
  leaves.forEach((leaf) => {
    const group = leafGroups.get(leaf.id);
    if (group) {
      group.rotation.z = leaf.rotationSign * nextAngle;
      group.updateMatrixWorld();
    }
  });
}

export default Door3DAssemblyMesh;
